import { useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal, MaskedLine } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Footwear", "Bags & Accessories", "Apparel", "Jewelry", "Other / Not sure yet"];
const STAGES = ["Launching my first collection", "Testing a new product", "Producing a limited run", "Scaling from small quantities", "Looking for a better manufacturing partner"];

const inputCls =
  "w-full border border-stone-300 bg-transparent px-5 py-4 text-sm text-ink placeholder:text-stone-400 outline-none transition-colors duration-300 focus:border-terra";

const Field = ({ label, children, id }) => (
  <div>
    <label htmlFor={id} className="mb-3 block font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500">
      {label}
    </label>
    {children}
  </div>
);

const NEXT_STEPS = [
  { n: "01", t: "Tell us about the product", d: "Share what you're building — references, quantities, timelines. Rough is fine." },
  { n: "02", t: "We reply within 48 hours", d: "A real reply, from a real person — with honest first thoughts on feasibility and fit." },
  { n: "03", t: "A focused first call", d: "We map your product, quantities and budget to the right sourcing path in India." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", brand: "", email: "", website: "", category: "", stage: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — we've received your project. Expect a reply within 48 hours.");
      setForm({ name: "", brand: "", email: "", website: "", category: "", stage: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please email nupur@obofficial.in instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <section data-testid="contact-hero" className="bg-ivory pt-36 md:pt-48">
        <div className="mx-auto max-w-7xl px-6 pb-16 md:px-12 md:pb-20">
          <Reveal>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-500">Start a Project</p>
          </Reveal>
          <h1 className="mt-8 max-w-4xl font-serif text-5xl leading-[1.05] tracking-tight text-ink md:text-7xl">
            <MaskedLine delay={0.2}>Thinking about sourcing</MaskedLine>
            <MaskedLine delay={0.32}>
              from India? <span className="italic text-terra">Let's talk product.</span>
            </MaskedLine>
          </h1>
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-stone-600 md:text-lg">
              Whether you're developing your first collection or looking for a better manufacturing partner —
              tell us what you're building.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="contact-body" className="bg-ivory pb-28 md:pb-40">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-7">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-8 border border-stone-200 bg-stone-100/60 p-8 md:p-12">
                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Your name" id="name">
                    <input id="name" data-testid="form-name" required value={form.name} onChange={set("name")} placeholder="Jane Cooper" className={inputCls} />
                  </Field>
                  <Field label="Brand name" id="brand">
                    <input id="brand" data-testid="form-brand" required value={form.brand} onChange={set("brand")} placeholder="Your brand" className={inputCls} />
                  </Field>
                </div>
                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Email" id="email">
                    <input id="email" data-testid="form-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@brand.com" className={inputCls} />
                  </Field>
                  <Field label="Website / Instagram (optional)" id="website">
                    <input id="website" data-testid="form-website" value={form.website} onChange={set("website")} placeholder="yourbrand.com" className={inputCls} />
                  </Field>
                </div>
                <div className="grid gap-8 sm:grid-cols-2">
                  <Field label="Product category" id="category">
                    <select id="category" data-testid="form-category" required value={form.category} onChange={set("category")} className={inputCls}>
                      <option value="" disabled>Select a category</option>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                  <Field label="Where are you right now?" id="stage">
                    <select id="stage" data-testid="form-stage" required value={form.stage} onChange={set("stage")} className={inputCls}>
                      <option value="" disabled>Select your stage</option>
                      {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Tell us about the product" id="message">
                  <textarea
                    id="message"
                    data-testid="form-message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What are you making? Rough quantities, materials, timelines — anything you already know."
                    className={`${inputCls} resize-none`}
                  />
                </Field>
                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={sending}
                  className="group inline-flex items-center gap-3 bg-terra px-10 py-5 font-label text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra-dark disabled:opacity-60"
                >
                  {sending ? "Sending" : "Start the Conversation"}
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
              </form>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <div className="md:sticky md:top-32">
              <Reveal delay={0.1}>
                <p className="font-label text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">What happens next</p>
              </Reveal>
              <div className="mt-8 space-y-10">
                {NEXT_STEPS.map((s, i) => (
                  <Reveal key={s.n} delay={0.12 + i * 0.08}>
                    <div className="flex gap-6" data-testid={`next-step-${s.n}`}>
                      <span className="font-serif text-3xl font-light italic text-terra">{s.n}</span>
                      <div>
                        <h3 className="font-serif text-xl text-ink">{s.t}</h3>
                        <p className="mt-2 text-sm leading-relaxed text-stone-600">{s.d}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.4}>
                <div className="mt-14 border-t border-stone-200 pt-8">
                  <p className="font-label text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">Prefer email?</p>
                  <a
                    href="mailto:nupur@obofficial.in"
                    data-testid="contact-email-link"
                    className="link-line mt-4 inline-block font-serif text-2xl text-ink transition-colors duration-300 hover:text-terra"
                  >
                    nupur@obofficial.in
                  </a>
                  <p className="mt-6 max-w-sm text-sm leading-relaxed text-stone-500">
                    You don't need massive volumes to work with the right factory. First collections and
                    small MOQs are welcome here.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
